import React from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { Icon, sparkleSolid, useIsMobile } from '@amzn/storm-ui';
import { selectEmbeddedChatOpen, setEmbeddedChatOpen } from 'src/app/slice';
import { useChromeConfigContext } from 'src/providers/ChromeConfigContextProvider';
import { useIsFeatureEnabled, useAdsAgentToggleTakt } from 'src/commons/hooks';
import { WeblabedFeatures } from 'src/commons/constants';
import { isUcbEverywherePath } from 'src/commons/utils/a3cChatUrlRules';
import { isA3cChatDisabled } from 'src/commons/utils/a3cChatGate';
import {
  SidenavBundleProvider,
  useSidenavBundle,
} from 'src/providers/PantherLocalizationProvider';
import {
  ACTIVE_STATE_ATTR,
  AdsAgentIconWrapper,
} from 'src/features/nav/components/ConsolidatedSideNav/AdsAgentNavButton';

/**
 * Bare icon button: no visible button chrome, just the circled sparkle.
 * The default focus outline is intentionally kept for accessibility.
 */
const IconOnlyButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  background: none;
  border: none;
  padding: 0;
  margin-inline-start: ${({ theme }) => theme.spacing.small.default};
  cursor: pointer;
`;

/**
 * Separator bar between the toggle and the page header's close button.
 * Matches storm-ui-page-header's internal VerticalDivider (not publicly
 * exported): 1px subtle border, 90% height, xxsmall inline margins.
 */
const Separator = styled.div`
  width: 1px;
  height: 90%;
  align-self: center;
  border-inline-start: ${({ theme }) => theme.border.subtle.default};
  margin-inline: ${({ theme }) => theme.spacing.xxsmall.default};
`;

/**
 * Single source of truth for whether the Focus-header Ads Agent toggle
 * (and its layout accommodations in FocusHeader) should render.
 *
 * Controlled ONLY by the dedicated A3C_EVERYWHERE_UCB_1459740 weblab —
 * deliberately independent of A3C_EVERYWHERE / A3C_EVERYWHERE_T2 so this
 * feature dials up on its own schedule. Additional guards:
 * - UCB everywhere paths only (/cb and /create/settings/st, see
 *   isUcbEverywherePath)
 * - desktop only (embedded chat does not exist on mobile)
 * - respects the page-level A3C opt-outs (the AAO unified gate vetoing chat via
 *   window.isA3CEnabled, loadBehavior DISABLED and the chat-disabled URL list)
 *
 * FocusHeader consumes this hook so that NO layout change (icon slot,
 * separator, close-button padding override) leaks to focus-mode pages of
 * other applications when the toggle is hidden.
 */
export const useShowFocusHeaderAdsAgentToggle = (): boolean => {
  const a3cConfig = useChromeConfigContext()?.a3cConfig;
  const isMobile = useIsMobile();
  const isUcbToggleEnabled = useIsFeatureEnabled(
    WeblabedFeatures.A3C_EVERYWHERE_UCB,
  );

  return (
    !isMobile &&
    !isA3cChatDisabled(a3cConfig) &&
    isUcbToggleEnabled &&
    isUcbEverywherePath(window.location.pathname)
  );
};

/**
 * Header toggle for the A3C (Ads Agent) embedded chat panel in Focus mode.
 *
 * Focus-mode pages (e.g. the campaign builders under /cb/*) have no side
 * nav, so AdsAgentNavButton is unavailable there and users had no way to
 * open or close the chat panel. This button fills that gap: it drives the
 * same Redux state (embeddedChat.isOpen). EmbeddedChatManager reacts to the
 * state change and shows/hides the panel with the correct focus-mode
 * layout, so this component never touches the DOM or panel sizing itself.
 *
 * Presentation-only: visibility is decided by
 * useShowFocusHeaderAdsAgentToggle (consumed by FocusHeader), so the
 * icon slot and padding adjustments in FocusHeader stay in sync with the
 * button itself.
 *
 * Icon-only: the nav button's REBRAND treatment (AdsAgentIconWrapper with
 * the bundled sparkle glyph) — a circle that fills with the AI accent color
 * while the panel is open. The legacy remote-SVG branch is not mirrored.
 *
 * Chrome-owned by design: page frameworks (e.g. AtlasFormRenderer) rewrite
 * the pageHeader objectInfo actions list through the chromeConfig API, so
 * this button must NOT be registered in that actions list — it would be
 * wiped on every page-side update.
 */
const FocusHeaderAdsAgentButtonInner: React.VFC = () => {
  const dispatch = useDispatch();
  const isOpen = useSelector(selectEmbeddedChatOpen);
  const bundle = useSidenavBundle();

  const logToggle = useAdsAgentToggleTakt('focus-header');

  const handleClick = () => {
    const nextState = !isOpen;
    logToggle(nextState);
    // EmbeddedChatManager owns panel visibility: its effect reacts to this
    // state change and calls setEmbeddedChatVisible with focus-mode-aware
    // arguments (width, margin handling), so dispatching is all we do here.
    dispatch(setEmbeddedChatOpen(nextState));
  };

  const label = bundle.getMessage('sidenav-ads-agent-label');

  return (
    <>
      <IconOnlyButton
        type="button"
        data-ccx-e2e-id="ads-agent-focus-header-button"
        onClick={handleClick}
        aria-pressed={isOpen}
        aria-label={label}
        title={label}
      >
        <AdsAgentIconWrapper
          isOpen={isOpen}
          isRebrand
          {...{ [ACTIVE_STATE_ATTR]: isOpen ? 'true' : 'false' }}
        >
          <Icon
            data-testid="ads-agent-focus-header-glyph"
            icon={sparkleSolid}
          />
        </AdsAgentIconWrapper>
      </IconOnlyButton>
      <Separator
        role="separator"
        aria-orientation="vertical"
        data-testid="ads-agent-focus-header-separator"
      />
    </>
  );
};

/**
 * Wraps the button in the sidenav Panther bundle provider so it reuses the
 * already-localized `sidenav-ads-agent-label` string (no new translations).
 */
export const FocusHeaderAdsAgentButton: React.VFC = () => (
  <SidenavBundleProvider>
    <FocusHeaderAdsAgentButtonInner />
  </SidenavBundleProvider>
);

export default FocusHeaderAdsAgentButton;
